// Редовете на фактурата при издаване — чистата логика, изнесена от
// `InvoiceCreateModal` (строене от сметките, редакция, обобщаване, проверката
// на сумата и тялото на `POST /invoices`).
//
// Бакшишът (docs/invoicing.md §Потоци — „Бакшиш във фактурата"):
// - по ЕДИН заключен ред „tip" на всяка сметка с бакшиш, с името от снимката
//   при закриването (`tip_invoice_name`) и `sourceBillIds: [id]` — BE го
//   проверява ТОЧНО срещу записания бакшиш и сам НЕ добавя редове (иначе
//   бакшишът би влязъл два пъти);
// - редът за бакшиш не се редактира и не се изтрива;
// - ставката на реда е от снимката (`billTipInvoiceVatRateOf`): 0 за група А
//   (без ДДС — извън ДДС групите, в `tipTotal`), 20/9 за група Б/В (облагаем ред
//   в групата на ставката си, без бележката „ДДС не се начислява");
// - обобщаването слива продуктовите редове по ставка (както досега) и НИКОГА
//   не слива бакшиш с продукт; редовете за бакшиш се сливат само помежду си
//   и само при еднакви име, третиране и ставка;
// - очакваната сума е Σ(поръчано + бакшиш − депозит) в цели центове.
// Без бакшиш всичко е точно досегашното поведение.
//
// Депозитът (BE docs/deposits.md §Фактура): сметка, платена с депозит, носи
// ЗАКЛЮЧЕН ред `deposit` „Приспаднато авансово плащане (депозит № N)" с МИНУС
// `deposit_amount` — авансът е фискализиран (Бон 1) и фактуриран отделно,
// дължимото по тази фактура е остатъкът. Ставката е тази на „ЧАСТИЧНИ
// ПЛАЩАНИЯ" — `deposit_vat_rate` на сметката (20 % / Б, а при фирма без ДДС
// регистрация 0 % / А; без полето — по-старо BE — 20). BE проверява ставката
// и че сумата му е точно −`deposit_amount`
// на сметката от `sourceBillIds`, и сам пише името му с номерата на бона и
// фактурата на аванса (BE `invoices/deposit_lines.py`). Обобщаването никога не
// го слива.

import type { BillHistory } from "../../../../../api/reportsBillsHistory";
import type {
  InvoiceLine,
  InvoiceLineKind,
  InvoiceLinePayload,
} from "../../../../../api/invoices";
import type { VatLineInput } from "../../../../../lib/vat";
import {
  billTipCentsOf,
  billTipInvoiceNameOf,
  billTipInvoiceVatRateOf,
  billTipTreatmentOf,
} from "../../../../../lib/billTips";
import { formatCents, parseMoneyToCents } from "../../../../../utils/money";

/** Ред във фактурата. Експортиран — ползва се и от `InvoiceLineEditModal`. */
export type Line = {
  id: string;
  name: string;
  unit: string;
  vatRate: number;
  quantity: number;
  unitPrice: number;
  total: number; // винаги се рекалкулира от qty * unitPrice
  /** Липсва/`item` — продукт или ръчен ред; `tip` — заключеният бакшиш;
   *  `deposit` — заключеният отрицателен ред за депозита на сметка. */
  kind?: InvoiceLineKind;
  /** Само за `tip`/`deposit`: сметките, чийто бакшиш/депозит носи редът. */
  sourceBillIds?: number[];
  /** Само за `tip`: данъчното третиране от снимката (ключ за сливане). */
  taxTreatment?: string;
};

/** Това, което дава модалът „Промяна"/„Нов ред" — без id, сума и вид. */
export type LineDraft = Omit<Line, "id" | "total" | "kind" | "sourceBillIds" | "taxTreatment">;

/** Името и ДДС групата (id) на продаваем от менюто. */
export type MenuItemMeta = { name: string; dds: number | null };

const DEFAULT_UNIT = "бр.";

export const isTipLine = (line: Pick<Line, "kind">): boolean => line.kind === "tip";

export const isDepositLine = (line: Pick<Line, "kind">): boolean => line.kind === "deposit";

/** Записан при закриването — не се променя, не се трие, не се обобщава. */
export const isLockedLine = (line: Pick<Line, "kind">): boolean =>
  isTipLine(line) || isDepositLine(line);

/** Ред за бакшиш БЕЗ ДДС (група А) — „—" вместо ставка и бележката под името.
 *  Облагаемият бакшиш (група Б/В) се показва като продуктов ред. */
export const isNoVatTipLine = (line: Pick<Line, "kind" | "vatRate">): boolean =>
  isTipLine(line) && !(line.vatRate > 0);

export const hasTipLines = (lines: readonly Line[]): boolean => lines.some(isTipLine);

/** qty × ед. цена, закръглено до стотинка — както досега. */
export const recalcLineTotal = (line: Pick<Line, "quantity" | "unitPrice">): number => {
  const quantity = Number.isFinite(line.quantity) ? line.quantity : 0;
  const unitPrice = Number.isFinite(line.unitPrice) ? line.unitPrice : 0;
  return Number((quantity * unitPrice).toFixed(2));
};

/**
 * Продуктовите редове на сметките — продуктите и добавките им (непроменено
 * от преди бакшиша). Следата от разделяне (`is_split` без
 * `split_from_bill_id`) и сторнираните редове се пропускат.
 *
 * Продукт или добавка, които не са продажба, не са на фактурата — както не са
 * и на касовия бон: цена 0.00 по менюто (`price`) или цена, сменена на 0 през
 * „Промяна на цена“ (`custom_price` и сума 0). Със 100 % отстъпка редът остава.
 * Добавките се гледат поотделно: подарен продукт с платена добавка оставя
 * добавката.
 */
const isNotASale = (row: { price: string; total_sum: string; custom_price?: boolean }): boolean =>
  Number(row.price ?? 0) === 0 || (row.custom_price === true && Number(row.total_sum ?? 0) === 0);

export const buildItemLines = (
  bills: readonly BillHistory[],
  itemNameById: ReadonlyMap<number, MenuItemMeta>,
  vatRateById: ReadonlyMap<number, number>,
): Line[] => {
  const out: Line[] = [];
  bills.forEach((bill) => {
    bill.orders.forEach((order) => {
      // Skip split-out placeholder orders — BE-ът оставя на оригиналната
      // сметка елемент-дубликат (is_split=true, split_from_bill_id=null)
      // за продуктите, преместени в друга сметка. Ако не го пропуснем тук,
      // фактурата ще съдържа и преместените артикули и сумата ще е цялата
      // оригинална сметка вместо остатъка след разделяне.
      if (order.is_split && order.split_from_bill_id == null) return;
      order.items.forEach((it) => {
        if (it.is_cancelled) return;
        const meta = itemNameById.get(it.item_id);
        const vatRate = meta?.dds != null ? (vatRateById.get(meta.dds) ?? 0) : 0;
        const qty = Number(it.quantity ?? 0);
        const price = Number(it.price ?? 0);
        const sum = Number(it.total_sum ?? qty * price);
        if (!isNotASale(it)) {
          out.push({
            id: `${bill.id}-${order.id}-${it.id}`,
            name: it.item_names?.bg || meta?.name || `Артикул #${it.item_id}`,
            unit: DEFAULT_UNIT,
            vatRate,
            quantity: qty,
            unitPrice: price,
            total: sum,
          });
        }
        // Добавките (additions) се водят отделно в bill payload-а, но в
        // sum-ата на сметката (bill.total_amount) са включени. Ако ги
        // пропуснем тук, invoice grand ще е по-малък от bill total и
        // mismatch confirm-ът ще се появи погрешно. Добавка няма собствен
        // флаг „анулирана" — сторно на продукта маха целия ред (по-горе).
        it.additions?.forEach((add) => {
          const addMeta = itemNameById.get(add.addition_item_id);
          const addVat = addMeta?.dds != null ? (vatRateById.get(addMeta.dds) ?? 0) : 0;
          const addQty = Number(add.quantity ?? 0);
          if (isNotASale(add)) return;
          const addPrice = Number(add.price ?? 0);
          const addSum = Number(add.total_sum ?? addQty * addPrice);
          out.push({
            id: `${bill.id}-${order.id}-${it.id}-add-${add.id}`,
            name: add.item_names?.bg || addMeta?.name || `Добавка #${add.addition_item_id}`,
            unit: DEFAULT_UNIT,
            vatRate: addVat,
            quantity: addQty,
            unitPrice: addPrice,
            total: addSum,
          });
        });
      });
    });
  });
  return out;
};

/** Ред за бакшиш от сума в центове — количество 1, ед. цена = сумата, със
 *  ставката от снимката (0 — без ДДС). */
const tipLine = (
  id: string,
  name: string,
  taxTreatment: string,
  vatRate: number,
  cents: number,
  sourceBillIds: number[],
): Line => ({
  id,
  name,
  unit: DEFAULT_UNIT,
  vatRate,
  quantity: 1,
  unitPrice: cents / 100,
  total: cents / 100,
  kind: "tip",
  sourceBillIds,
  taxTreatment,
});

/** По ЕДИН ред за бакшиш на всяка сметка с бакшиш > 0, в реда на сметките. */
export const buildTipLines = (bills: readonly BillHistory[]): Line[] =>
  bills
    .filter((bill) => billTipCentsOf(bill) > 0)
    .map((bill) =>
      tipLine(
        `tip-${bill.id}`,
        billTipInvoiceNameOf(bill),
        billTipTreatmentOf(bill),
        billTipInvoiceVatRateOf(bill),
        billTipCentsOf(bill),
        [bill.id],
      ),
    );

/** Ставката на аванса, когато BE не я казва (по-старо BE без `deposit_vat_rate`):
 *  „ЧАСТИЧНИ ПЛАЩАНИЯ" на бона на депозита е група Б. */
const DEPOSIT_LINE_VAT_RATE = 20;

/** Платеното с депозит по сметката, в центове (0 без депозит). */
const billDepositCentsOf = (bill: Pick<BillHistory, "deposit_amount">): number =>
  Math.max(0, parseMoneyToCents(bill.deposit_amount ?? "0") ?? 0);

/** По ЕДИН отрицателен ред за всяка сметка, платена (изцяло или отчасти) с депозит. */
export const buildDepositLines = (bills: readonly BillHistory[]): Line[] =>
  bills
    .filter((bill) => billDepositCentsOf(bill) > 0)
    .map((bill) => {
      const amount = -billDepositCentsOf(bill) / 100;
      return {
        id: `deposit-${bill.id}`,
        name: bill.deposit_id
          ? `Приспаднато авансово плащане (депозит № ${bill.deposit_id})`
          : "Приспаднато авансово плащане (депозит)",
        unit: DEFAULT_UNIT,
        vatRate: bill.deposit_vat_rate ?? DEPOSIT_LINE_VAT_RATE,
        quantity: 1,
        unitPrice: amount,
        total: amount,
        kind: "deposit",
        sourceBillIds: [bill.id],
      };
    });

/** Началните редове: продуктите, после бакшишите и накрая депозитите (по един
 *  на сметка). */
export const buildInvoiceLines = (
  bills: readonly BillHistory[],
  itemNameById: ReadonlyMap<number, MenuItemMeta>,
  vatRateById: ReadonlyMap<number, number>,
): Line[] => [
  ...buildItemLines(bills, itemNameById, vatRateById),
  ...buildTipLines(bills),
  ...buildDepositLines(bills),
];

const lineCents = (line: Pick<Line, "total">): number => Math.round(line.total * 100);

/** Сливане на редовете за бакшиш само при еднакви име, третиране И ставка. */
const mergeTipLines = (tipLines: readonly Line[], newId: () => string): Line[] => {
  const groups = new Map<string, { first: Line; cents: number; sourceBillIds: number[] }>();
  for (const line of tipLines) {
    const key = `${line.name}\u0000${line.taxTreatment ?? ""}\u0000${line.vatRate}`;
    const group = groups.get(key);
    if (group) {
      group.cents += lineCents(line);
      group.sourceBillIds.push(...(line.sourceBillIds ?? []));
    } else {
      groups.set(key, {
        first: line,
        cents: lineCents(line),
        sourceBillIds: [...(line.sourceBillIds ?? [])],
      });
    }
  }
  return Array.from(groups.values()).map(({ first, cents, sourceBillIds }) =>
    tipLine(newId(), first.name, first.taxTreatment ?? "", first.vatRate, cents, sourceBillIds),
  );
};

/**
 * „Всичко на един ред": продуктовите редове → по един „Консумация" на ДДС
 * ставка (точно досегашното смятане), после бакшишите — слети само помежду
 * си при еднакво име и третиране, никога в продуктов ред; редовете за депозит
 * остават както са (всеки сочи своята сметка).
 */
export const summarizeInvoiceLines = (
  lines: readonly Line[],
  summarizedName: string,
  newId: () => string,
): Line[] => {
  // Групираме по ДДС ставка → един обобщен ред на група. Така ако
  // имаме напр. храна (9%) и алкохол (20%), получаваме 2 реда.
  const byRate = new Map<number, number>();
  lines.forEach((line) => {
    if (isLockedLine(line)) return;
    byRate.set(line.vatRate, (byRate.get(line.vatRate) ?? 0) + line.total);
  });
  // Подреждаме по ставка във възходящ ред — стабилен output.
  const collapsedLines: Line[] = Array.from(byRate.entries())
    .sort(([a], [b]) => a - b)
    .map(([rate, total]) => ({
      id: newId(),
      name: summarizedName,
      unit: DEFAULT_UNIT,
      vatRate: rate,
      quantity: 1,
      unitPrice: Number(total.toFixed(2)),
      total: Number(total.toFixed(2)),
    }));
  return [
    ...collapsedLines,
    ...mergeTipLines(lines.filter(isTipLine), newId),
    ...lines.filter(isDepositLine),
  ];
};

/** Промяна на ред — total винаги = qty × ед. цена; бакшишът и депозитът са заключени. */
export const patchInvoiceLine = (
  lines: readonly Line[],
  id: string,
  patch: Partial<LineDraft>,
): Line[] =>
  lines.map((line) => {
    if (line.id !== id || isLockedLine(line)) return line;
    const merged = { ...line, ...patch };
    merged.total = recalcLineTotal(merged);
    return merged;
  });

/** Изтриване на ред — редовете за бакшиш и за депозит не се трият. */
export const removeInvoiceLine = (lines: readonly Line[], id: string): Line[] =>
  lines.filter((line) => line.id !== id || isLockedLine(line));

/** Входът на ДДС разбивката (`lib/vat.ts`) — бакшишът без ДДС е извън
 *  групите, облагаемият е в групата на ставката си. */
export const toVatInputs = (lines: readonly Line[]): VatLineInput[] =>
  lines.map((line) =>
    isTipLine(line)
      ? { gross: line.total, rate: line.vatRate, kind: "tip" }
      : { gross: line.total, rate: line.vatRate },
  );

/** Входът на ДДС разбивката за ИЗДАДЕНА фактура (A4 прегледът) — редът за
 *  бакшиш (`line_kind: "tip"`) със записаната си ставка (0 — извън групите);
 *  стари фактури — както досега. */
export const toStoredLineVatInputs = (lines: readonly InvoiceLine[]): VatLineInput[] =>
  lines.map((line) =>
    line.line_kind === "tip"
      ? { gross: Number(line.total), rate: Number(line.vat_rate) || 0, kind: "tip" }
      : { gross: Number(line.total), rate: Number(line.vat_rate) },
  );

/** Очакваната сума на фактурата: Σ(поръчано + бакшиш − платеното с депозит) на
 *  сметките, в центове — депозитът е фактуриран отделно (редът `deposit`). */
export const expectedBillsTotalCents = (bills: readonly BillHistory[]): number =>
  bills.reduce(
    (sum, bill) =>
      sum +
      (parseMoneyToCents(bill.total_amount) ?? 0) +
      billTipCentsOf(bill) -
      billDepositCentsOf(bill),
    0,
  );

/**
 * Разминава ли се общата стойност на фактурата със сметките — със същия праг
 * от една стотинка като досега, но в цели центове (без шума на float-а).
 */
export const isInvoiceTotalMismatch = (
  bills: readonly BillHistory[],
  grandTotal: number,
): boolean => Math.abs(expectedBillsTotalCents(bills) - Math.round(grandTotal * 100)) > 1;

/** Сумата като низ „x.xx" — за диалога при разминаване. */
export const formatExpectedBillsTotal = (bills: readonly BillHistory[]): string =>
  formatCents(expectedBillsTotalCents(bills));

/**
 * Редът в тялото на `POST /invoices`. Продуктовият ред е точно досегашните шест
 * ключа; редовете за бакшиш и за депозит пращат сумите като низове (депозитът
 * — с минус) и `line_kind` + `source_bill_ids` (BE ги проверява срещу сметките).
 */
export const toInvoiceLinePayload = (line: Line): InvoiceLinePayload => {
  if (isDepositLine(line)) {
    const amount = formatCents(lineCents(line)); // „-50.00"
    return {
      name: line.name,
      unit: line.unit,
      vat_rate: line.vatRate,
      quantity: 1,
      unit_price: amount,
      total: amount,
      line_kind: "deposit",
      source_bill_ids: [...(line.sourceBillIds ?? [])],
    };
  }
  if (!isTipLine(line)) {
    return {
      name: line.name,
      unit: line.unit,
      vat_rate: line.vatRate,
      quantity: line.quantity,
      unit_price: line.unitPrice,
      total: line.total,
    };
  }
  const amount = formatCents(lineCents(line));
  return {
    name: line.name,
    unit: line.unit,
    vat_rate: line.vatRate,
    quantity: 1,
    unit_price: amount,
    total: amount,
    line_kind: "tip",
    source_bill_ids: [...(line.sourceBillIds ?? [])],
  };
};
